import * as React from "react";
import { useState } from "react";
import styled from "@mui/material/styles/styled";
import { useTranslate } from "../hooks/useTranslate";
import { Fade, Grow, IconButton, Theme } from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";

import { ReactComponent as ThumbsUp } from "../assets/thumbsUp.svg";
import { ReactComponent as ThumbsUpFilled } from "../assets/thumbsUpFilled.svg";
import { ReactComponent as ThumbsDown } from "../assets/thumbsDown.svg";
import { ReactComponent as ThumbsDownFilled } from "../assets/thumbsDownFilled.svg";
import { PatientColorMap } from "../layout/patientColorMap";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { PatientTextField } from "./uiControllers/patientTextField";
import { GenericButton } from "./uiControllers/genericButton";
import { RoundedPatientTitleBox } from "./uiControllers/roundedPatientTitleBox";
import { useClinicData } from "../clinic/clinicHooks";

//region [[ Styles ]]

const FeedbackContainerView = styled(RoundedPatientTitleBox)({
  justifyContent: "center",
  alignItems: "center",
  marginBottom: 0,
});

const SubTitle = styled("div")(({ theme }) => ({
  fontWeight: 500,
  marginBottom: "10px",
  fontSize: "15px",
  textAlign: "center",
  color: PatientColorMap.Default,
  "&.center": {
    marginTop: "50px",
    marginBottom: "30px",
  },
}));

const ThumbsIconsContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexFlow: "row",
  justifyItems: "center",
  justifyContent: "center",
});

const ThumbsUpFilledStyled = styled(ThumbsUpFilled)(({ theme }) => ({
  "& path:first-of-type": {
    fill: PatientColorMap.PrimaryLight,
  },
  "& path:last-of-type": {
    fill: PatientColorMap.Primary,
  },
}));

const ThumbsUpStyled = styled(ThumbsUp)(({ theme }) => ({
  "& fill:last-of-type": {
    stroke: PatientColorMap.Primary,
  },
}));

const ThumbsDownFilledStyled = styled(ThumbsDownFilled)(({ theme }) => ({
  "& path:first-of-type": {
    fill: PatientColorMap.PrimaryLight,
  },
  "& path:last-of-type": {
    fill: PatientColorMap.Primary,
  },
}));
const ThumbsDownStyled = styled(ThumbsDown)(({ theme }) => ({
  "& fill:last-of-type": {
    stroke: PatientColorMap.Primary,
  },
}));

const LikeDiseaseButton = styled(IconButton)(({ theme }) => ({
  boxShadow: "0px 1.56px 6.26px 0px #00000014",
  borderRadius: "4px",
  margin: "0 4.3px",
  "&.like-button": {
    "&, &:active, &:focus, &:hover": {
      background: "none",
      padding: "10px",
    },
  },
}));

const FeedbackInput = styled(PatientTextField)({
  marginBottom: "10px",
});

const FeedbackTextContainer = styled("div")({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
});

const SubmitButton = styled(GenericButton)({
  maxWidth: "120px",
});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      backgroundColor: "white",
      fontSize: "16px",
      width: "100%",

      fontWeight: 500,
      height: "100px",
    },
  }),
);

const SendFeedbackForm = styled("div")({});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface FeedbackContainerProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const FeedbackContainer = ({ ...props }: FeedbackContainerProps) => {
  const { translate } = useTranslate();
  const { name: clinicName, clinic: clinicId } = useClinicData();

  const [liked, setLiked] = useState(false);

  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);

  const [disliked, setDisliked] = useState(false);

  const { partnerId } = useWidgetSettings();

  const classes = useStyles();

  const onLike = async () => {
    setLiked(!liked);
    setDisliked(false);

    evt({
      category: CATEGORY.PATIENT_RESULT,
      action: ACTION.ASSESSMENT_HELPFUL,
      partnerId,
      props: {
        clinicName,
        clinicId,
      },
    });
  };

  const onDislike = async () => {
    setLiked(false);
    setDisliked(!disliked);

    evt({
      category: CATEGORY.PATIENT_RESULT,
      action: ACTION.ASSESSMENT_NOT_HELPFUL,
      partnerId,
      props: {
        clinicName,
        clinicId,
      },
    });
  };

  const onFeedback = (event) => {
    const value = event.target.value;
    setFeedbackText(value);
  };

  const onSubmit = (event) => {
    if (!feedbackSent) {
      setFeedbackSent(true);
      evt({
        category: CATEGORY.PATIENT_RESULT,
        action: ACTION.PATIENT_SEND_FEEDBACK,
        label: feedbackText,
        partnerId,
        props: {
          clinicName,
          clinicId,
        },
      });
    }
  };

  const engaged = disliked || liked;

  return (
    <FeedbackContainerView title={translate("FEEDBACK_TITLE")}>
      <Grow in={!feedbackSent} mountOnEnter unmountOnExit timeout={500}>
        <SendFeedbackForm>
          <SubTitle>
            {translate(!engaged ? "FEEDBACK_SUBTITLE" : "FEEDBACK_DESCRIPTION")}
          </SubTitle>
          <Grow in={!engaged} unmountOnExit timeout={100}>
            <ThumbsIconsContainer>
              <LikeDiseaseButton
                className="like-button"
                onClick={onLike}
                size={"medium"}
                key={"like" + liked.toString()}
              >
                {liked ? <ThumbsUpFilledStyled /> : <ThumbsUpStyled />}
              </LikeDiseaseButton>

              <LikeDiseaseButton
                className="like-button"
                key={"dislike" + disliked.toString()}
                onClick={onDislike}
                size={"medium"}
              >
                {disliked ? <ThumbsDownFilledStyled /> : <ThumbsDownStyled />}
              </LikeDiseaseButton>
            </ThumbsIconsContainer>
          </Grow>
          <Grow in={engaged} mountOnEnter timeout={300}>
            <FeedbackTextContainer>
              <FeedbackInput
                variant={"outlined"}
                onChange={onFeedback}
                value={feedbackText}
                InputProps={{
                  classes: {
                    root: classes.input,
                  },
                }}
                rows={4}
                multiline
              />
              <SubmitButton onClick={onSubmit} label={translate("SEND")} />
            </FeedbackTextContainer>
          </Grow>
        </SendFeedbackForm>
      </Grow>
      <Fade in={feedbackSent} mountOnEnter unmountOnExit>
        <SubTitle className="center">
          {translate("THANKS_ENTER_SYMPTOM")}
        </SubTitle>
      </Fade>
    </FeedbackContainerView>
  );
};
